"use client";

import { useEffect, useId, useRef, useState, type MouseEvent, type ReactNode } from "react";
import type { HeaderMenuItem } from "./navigation";
import type { NavLink } from "./types";

const iconPaths: Record<string, string> = {
  sparkles: "m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5Z",
  building: "M5 21V3h14v18ZM9 7h1m4 0h1M9 11h1m4 0h1M9 15h1m4 0h1m-4 6v-3h2v3",
  sun: "M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0",
  layers: "m12 3 10 5-10 5L2 8Zm-10 10 10 5 10-5M2 18l10 5 10-5",
  bath: "M4 12V5a3 3 0 0 1 6 0m-3 3h6M2 12h20l-2 7H4Zm4 7-1 3m13-3 1 3",
  shield: "m12 2 9 4v6c0 5-9 10-9 10S3 17 3 12V6Zm-4 10 3 3 5-6",
  window: "M3 3h18v18H3Zm9 0v18M3 12h18",
  grid: "M3 3h7v7H3Zm11 0h7v7h-7ZM3 14h7v7H3Zm11 0h7v7h-7Z",
  leaf: "M3 21 17 7M5 19C-1 4 13 1 21 3c2 8-1 22-16 16Z",
  briefcase: "M8 6V3h8v3M3 6h18v15H3Zm0 6h18m-11 0v3h4v-3",
  camera: "M3 6h4l2-3h6l2 3h4v15H3ZM16 13a4 4 0 1 1-8 0 4 4 0 0 1 8 0",
};

function MenuIcon({ name }: { name: NavLink["icon"] }) {
  return name && iconPaths[name] ? <svg className="kate-menu-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d={iconPaths[name]}/></svg> : null;
}

/** Receives resolved, serializable links; never imports server credentials or renders arbitrary HTML. */
export function HeaderMenu({ items, currentHref, locale, mobile = false, onNavigate, extra, buttonHref }: {
  items: HeaderMenuItem[]; currentHref: string; locale: import("./site-languages").SiteLocale; mobile?: boolean;
  onNavigate?: (event: MouseEvent<HTMLAnchorElement>) => void; extra?: ReactNode;
  buttonHref?: string;
}) {
  const root = useRef<HTMLElement>(null);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const panels = useRef(new Map<string, HTMLUListElement>());
  const prefix = useId();
  const [open, setOpen] = useState<string | null>(null);
  const [alignEnd, setAlignEnd] = useState(false);
  useEffect(() => {
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(null); };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) { event.preventDefault(); buttons.current.get(open)?.focus(); setOpen(null); }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);
  useEffect(() => {
    if (!open || mobile) return;
    const panel = panels.current.get(open);
    if (!panel) return;
    const bound = root.current?.closest(".preview-frame")?.getBoundingClientRect();
    const right = bound?.right ?? window.innerWidth;
    setAlignEnd(panel.parentElement!.getBoundingClientRect().left + panel.offsetWidth > right - 12);
  }, [open, mobile]);
  const clickLink = (event: MouseEvent<HTMLAnchorElement>) => {
    onNavigate?.(event); setOpen(null);
    root.current?.closest("details.mobile-navigation")?.removeAttribute("open");
  };
  const link = (item: NavLink) => <a href={item.href} className={buttonHref && item.href.replace(/\/+$/, "") === buttonHref.replace(/\/+$/, "") ? "kate-menu-cta" : undefined} aria-current={item.href === currentHref ? "page" : undefined} onClick={clickLink}><MenuIcon name={item.icon}/><span>{item.label}</span></a>;
  return <nav ref={root} className={`kate-header-menu ${mobile ? "kate-menu-mobile" : "kate-menu-desktop"}`} aria-label={locale === "el" ? (mobile ? "Μενού κινητού" : "Κύρια πλοήγηση") : (mobile ? "Mobile navigation" : "Main navigation")} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(null); }}>
    <ul className="kate-menu-roots">{items.map((item, index) => {
      const expanded = open === item.id;
      const panelId = `${prefix}-submenu-${index}`;
      return <li key={item.id} className={`kate-menu-item ${expanded ? "is-expanded" : ""} ${expanded && alignEnd ? "align-end" : ""}`} data-menu-id={item.id} onPointerEnter={event => {
        if (!mobile && event.pointerType === "mouse" && window.matchMedia("(hover: hover) and (pointer: fine)").matches) setOpen(item.children.length ? item.id : null);
      }} onPointerLeave={() => { if (!mobile && !root.current?.contains(document.activeElement)) setOpen(null); }}>
        <div className="kate-menu-top">{item.href && link(item)}{item.children.length > 0 && <button type="button" className={`kate-menu-toggle ${!item.href ? "group-only" : ""}`} ref={node => { if (node) buttons.current.set(item.id, node); else buttons.current.delete(item.id); }} aria-label={item.href ? `${locale === "el" ? "Υπομενού" : "Submenu"}: ${item.label}` : undefined} aria-expanded={expanded} aria-controls={panelId} onClick={() => { setAlignEnd(false); setOpen(expanded ? null : item.id); }} onKeyDown={event => {
          if (event.key === "ArrowDown") {
            event.preventDefault(); setOpen(item.id);
            window.setTimeout(() => panels.current.get(item.id)?.querySelector("a")?.focus(), 0);
          }
        }}>{!item.href && <><MenuIcon name={item.icon}/><span>{item.label}</span></>}<svg aria-hidden="true" viewBox="0 0 16 16" className="kate-menu-chevron"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8"/></svg></button>}</div>
        {item.children.length > 0 && <ul id={panelId} className="kate-menu-panel" hidden={!expanded} ref={node => { if (node) panels.current.set(item.id, node); else panels.current.delete(item.id); }} onKeyDown={event => {
          if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
          const anchors = Array.from(event.currentTarget.querySelectorAll("a"));
          const active = anchors.indexOf(document.activeElement as HTMLAnchorElement);
          event.preventDefault();
          anchors[event.key === "Home" ? 0 : event.key === "End" ? anchors.length - 1 : Math.max(0, Math.min(anchors.length - 1, active + (event.key === "ArrowDown" ? 1 : -1)))]?.focus();
        }}>{item.children.map(child => <li key={child.id}>{link(child)}</li>)}</ul>}
        {item.children.length > 0 && <noscript><ul className="kate-menu-fallback">{item.children.map(child => <li key={child.id}>{link(child)}</li>)}</ul></noscript>}
      </li>;
    })}</ul>{extra}
  </nav>;
}
